/**
 * Figure library for the studio: the geometry of every 2D figure and pseudo-3D object,
 * defined ONCE and used by three consumers — the live SVG layer, the canvas export renderer
 * (save / share / print / stamps) and the picker icons — so all of them always agree.
 *
 *   SHAPES_2D[key] = { title, path(w, h) }            -> SVG path "d" inside a 0..w × 0..h box
 *   SHAPES_3D[key] = { title, parts(w, h) }           -> [{ d, fill }]  fill: 'light' | 'base' |
 *                                                          'dark' | 'radial' | 'hgrad' | 'vgrad'
 *   shade(hex, amount)                                -> lighter (>0) / darker (<0) colour
 *
 * The classic five (line, arrow, circle, triangle, rectangle) keep their original renderers in
 * ImageStudio.jsx; everything else goes through here.
 */

const P = (n) => Math.round(n * 100) / 100;
const pt = (x, y) => `${P(x)},${P(y)}`;
const poly = (pts) => 'M' + pts.map(([x, y]) => pt(x, y)).join('L') + 'Z';
const ellipsePath = (cx, cy, rx, ry) =>
  `M${pt(cx - rx, cy)}A${P(rx)} ${P(ry)} 0 1 0 ${pt(cx + rx, cy)}A${P(rx)} ${P(ry)} 0 1 0 ${pt(cx - rx, cy)}Z`;

/** Regular polygon / star inscribed in the box. inner = inner-radius ratio for stars. */
const star = (w, h, n, inner = null, rotate = -Math.PI / 2) => {
  const cx = w / 2, cy = h / 2, rx = w / 2, ry = h / 2;
  const pts = [];
  const steps = inner ? n * 2 : n;
  for (let i = 0; i < steps; i++) {
    const a = rotate + (i / steps) * Math.PI * 2;
    const k = inner && i % 2 ? inner : 1;
    pts.push([cx + rx * k * Math.cos(a), cy + ry * k * Math.sin(a)]);
  }
  return poly(pts);
};

const roundedRect = (x, y, w, h, r) => {
  r = Math.min(r, w / 2, h / 2);
  return `M${pt(x + r, y)}H${P(x + w - r)}A${P(r)} ${P(r)} 0 0 1 ${pt(x + w, y + r)}V${P(y + h - r)}A${P(r)} ${P(r)} 0 0 1 ${pt(x + w - r, y + h)}H${P(x + r)}A${P(r)} ${P(r)} 0 0 1 ${pt(x, y + h - r)}V${P(y + r)}A${P(r)} ${P(r)} 0 0 1 ${pt(x + r, y)}Z`;
};

/** A cloud-like blob: outward arcs between points around an ellipse. */
const cloud = (x, y, w, h, bumps = 7) => {
  const cx = x + w / 2, cy = y + h / 2, rx = w / 2, ry = h / 2;
  const pts = [];
  for (let i = 0; i < bumps; i++) {
    const a = (i / bumps) * Math.PI * 2 + 0.3;
    const k = 0.86;
    pts.push([cx + rx * k * Math.cos(a), cy + ry * k * Math.sin(a)]);
  }
  let d = `M${pt(...pts[0])}`;
  for (let i = 0; i < bumps; i++) {
    const [ax, ay] = pts[i], [bx, by] = pts[(i + 1) % bumps];
    const r = Math.hypot(bx - ax, by - ay) * 0.58;
    d += `A${P(r)} ${P(r)} 0 0 1 ${pt(bx, by)}`;
  }
  return d + 'Z';
};

export const SHAPES_2D = {
  roundedRect: { title: 'Rounded rectangle', path: (w, h) => roundedRect(0, 0, w, h, Math.min(w, h) * 0.22) },
  pentagon:    { title: 'Pentagon',  path: (w, h) => star(w, h, 5) },
  hexagon:     { title: 'Hexagon',   path: (w, h) => star(w, h, 6, null, 0) },
  diamond:     { title: 'Diamond',   path: (w, h) => poly([[w / 2, 0], [w, h / 2], [w / 2, h], [0, h / 2]]) },
  rightTriangle: { title: 'Right triangle', path: (w, h) => poly([[0, 0], [0, h], [w, h]]) },
  blockArrow:  { title: 'Block arrow', path: (w, h) => poly([[w / 2, 0], [w, h * 0.45], [w * 0.7, h * 0.45], [w * 0.7, h], [w * 0.3, h], [w * 0.3, h * 0.45], [0, h * 0.45]]) },
  pointer:     { title: 'Pointer',   path: (w, h) => poly([[w / 2, 0], [w, h], [w / 2, h * 0.72], [0, h]]) },
  quarterCircle: { title: 'Quarter circle', path: (w, h) => `M${pt(0, h)}V0A${P(w)} ${P(h)} 0 0 1 ${pt(w, h)}Z` },
  star4:       { title: '4-point star', path: (w, h) => star(w, h, 4, 0.38) },
  star5:       { title: 'Star',      path: (w, h) => star(w, h, 5, 0.42) },
  star6:       { title: '6-point star', path: (w, h) => star(w, h, 6, 0.58) },
  burst:       { title: 'Burst',     path: (w, h) => star(w, h, 14, 0.76) },
  speechBubble: { title: 'Speech bubble', path: (w, h) => {
    const bh = h * 0.76, r = Math.min(w, h) * 0.18;
    const rr = roundedRect(0, 0, w, bh, r).slice(0, -1); // open: drop the Z
    // splice a tail into the bottom edge (bottom edge runs right→left)
    return rr.replace(`H${P(r)}`, `H${P(w * 0.4)}L${pt(w * 0.22, h)}L${pt(w * 0.24, bh)}H${P(r)}`) + 'Z';
  } },
  thoughtBubble: { title: 'Thought bubble', path: (w, h) =>
    cloud(0, 0, w, h * 0.72) + ellipsePath(w * 0.22, h * 0.83, w * 0.07, h * 0.06) + ellipsePath(w * 0.1, h * 0.95, w * 0.04, h * 0.04) },
  cross:       { title: 'Cross', path: (w, h) => {
    const t = 0.24; // arm thickness as a fraction
    const pts = [[t, 0], [0.5, 0.5 - t], [1 - t, 0], [1, t], [0.5 + t, 0.5], [1, 1 - t], [1 - t, 1], [0.5, 0.5 + t], [t, 1], [0, 1 - t], [0.5 - t, 0.5], [0, t]];
    return poly(pts.map(([x, y]) => [x * w, y * h]));
  } },
  check:       { title: 'Check mark', path: (w, h) => poly([[0, h * 0.58], [w * 0.2, h * 0.4], [w * 0.4, h * 0.62], [w * 0.82, h * 0.08], [w, h * 0.26], [w * 0.4, h]]) },
  moon:        { title: 'Moon', path: (w, h) =>
    `M${pt(w * 0.82, h * 0.1)}A${P(w / 2)} ${P(h / 2)} 0 1 0 ${pt(w * 0.82, h * 0.9)}A${P(w * 0.46)} ${P(h * 0.46)} 0 0 1 ${pt(w * 0.82, h * 0.1)}Z` },
  banner:      { title: 'Ribbon', path: (w, h) =>
    `M${pt(0, h * 0.18)}Q${pt(w * 0.25, 0)} ${pt(w * 0.5, h * 0.18)}T${pt(w, h * 0.18)}V${P(h * 0.82)}Q${pt(w * 0.75, h)} ${pt(w * 0.5, h * 0.82)}T${pt(0, h * 0.82)}Z` },
  lightning:   { title: 'Lightning', path: (w, h) => poly([[w * 0.62, 0], [w * 0.12, h * 0.56], [w * 0.44, h * 0.56], [w * 0.34, h], [w * 0.88, h * 0.4], [w * 0.56, h * 0.4]]) },
  heart:       { title: 'Heart', path: (w, h) =>
    `M${pt(w * 0.5, h * 0.96)}C${pt(w * 0.12, h * 0.68)} ${pt(0, h * 0.46)} ${pt(0, h * 0.28)}C${pt(0, h * 0.1)} ${pt(w * 0.14, 0)} ${pt(w * 0.3, 0)}C${pt(w * 0.4, 0)} ${pt(w * 0.47, h * 0.06)} ${pt(w * 0.5, h * 0.16)}C${pt(w * 0.53, h * 0.06)} ${pt(w * 0.6, 0)} ${pt(w * 0.7, 0)}C${pt(w * 0.86, 0)} ${pt(w, h * 0.1)} ${pt(w, h * 0.28)}C${pt(w, h * 0.46)} ${pt(w * 0.88, h * 0.68)} ${pt(w * 0.5, h * 0.96)}Z` },
  freeline:    { title: 'Freeform line — drag to draw, then drag the nodes', draw: 'freeform', closed: false,
                 path: (w, h) => `M${pt(0, h * 0.8)}C${pt(w * 0.2, h * 0.1)} ${pt(w * 0.35, h * 0.1)} ${pt(w * 0.5, h * 0.55)}S${pt(w * 0.8, h)} ${pt(w, h * 0.25)}`, open: true },
  freeshape:   { title: 'Freeform shape — drag to draw a closed shape, then drag the nodes', draw: 'freeform', closed: true,
                 path: (w, h) => `M${pt(w * 0.5, h * 0.05)}C${pt(w * 0.9, 0)} ${pt(w, h * 0.5)} ${pt(w * 0.85, h * 0.8)}C${pt(w * 0.7, h)} ${pt(w * 0.3, h)} ${pt(w * 0.15, h * 0.8)}C${pt(0, h * 0.6)} ${pt(w * 0.1, h * 0.1)} ${pt(w * 0.5, h * 0.05)}Z` },
};

/* ------------------------------------ 3D ---------------------------------- */

export const SHAPES_3D = {
  cube: { title: 'Cube', parts: (w, h) => {
    const d = w * 0.3, e = h * 0.25;
    return [
      { d: poly([[0, e], [w - d, e], [w - d, h], [0, h]]), fill: 'base' },
      { d: poly([[0, e], [d, 0], [w, 0], [w - d, e]]), fill: 'light' },
      { d: poly([[w - d, e], [w, 0], [w, h - e], [w - d, h]]), fill: 'dark' },
    ];
  } },
  sphere: { title: 'Sphere', parts: (w, h) => [{ d: ellipsePath(w / 2, h / 2, w / 2, h / 2), fill: 'radial' }] },
  hemisphere: { title: 'Hemisphere', parts: (w, h) => {
    const ry = h * 0.2, cy = h - ry;
    return [
      { d: ellipsePath(w / 2, cy, w / 2, ry), fill: 'dark' },
      { d: `M${pt(0, cy)}A${P(w / 2)} ${P(cy)} 0 0 1 ${pt(w, cy)}A${P(w / 2)} ${P(ry)} 0 0 0 ${pt(0, cy)}Z`, fill: 'radial' },
    ];
  } },
  cone: { title: 'Cone', parts: (w, h) => {
    const ry = h * 0.14, cy = h - ry;
    return [
      { d: ellipsePath(w / 2, cy, w / 2, ry), fill: 'dark' },
      { d: `M${pt(w / 2, 0)}L${pt(w, cy)}A${P(w / 2)} ${P(ry)} 0 0 1 ${pt(0, cy)}Z`, fill: 'hgrad' },
    ];
  } },
  pyramid: { title: 'Pyramid', parts: (w, h) => [
    { d: poly([[w / 2, 0], [0, h * 0.78], [w * 0.5, h]]), fill: 'light' },
    { d: poly([[w / 2, 0], [w * 0.5, h], [w, h * 0.78]]), fill: 'dark' },
  ] },
  cylinder: { title: 'Cylinder', parts: (w, h) => {
    const ry = Math.min(h * 0.14, w * 0.3);
    return [
      { d: `M${pt(0, ry)}A${P(w / 2)} ${P(ry)} 0 0 0 ${pt(w, ry)}V${P(h - ry)}A${P(w / 2)} ${P(ry)} 0 0 1 ${pt(0, h - ry)}Z`, fill: 'hgrad' },
      { d: ellipsePath(w / 2, ry, w / 2, ry), fill: 'light' },
    ];
  } },
  capsule: { title: 'Capsule', parts: (w, h) => {
    const r = Math.min(w / 2, h / 2);
    const vertical = h >= w;
    return [
      { d: roundedRect(0, 0, w, h, r), fill: vertical ? 'hgrad' : 'vgrad' },
      vertical
        ? { d: ellipsePath(w / 2, r * 0.9, w * 0.36, r * 0.36), fill: 'light' }
        : { d: ellipsePath(r * 0.9, h / 2, r * 0.36, h * 0.36), fill: 'light' },
    ];
  } },
  torus: { title: 'Ring', parts: (w, h) => [
    { d: ellipsePath(w / 2, h / 2, w / 2, h / 2) + ellipsePath(w / 2, h * 0.47, w * 0.24, h * 0.2), fill: 'hgrad', evenOdd: true },
    { d: `M${pt(w * 0.26, h * 0.47)}A${P(w * 0.24)} ${P(h * 0.2)} 0 0 0 ${pt(w * 0.74, h * 0.47)}A${P(w * 0.24)} ${P(h * 0.12)} 0 0 1 ${pt(w * 0.26, h * 0.47)}Z`, fill: 'dark' },
  ] },
  pipe: { title: 'Pipe', parts: (w, h) => {
    const t = Math.min(w, h) * 0.34;                 // tube thickness
    const ry = t * 0.38;                            // mouth ellipse ry
    // an elbow: vertical arm on the left, horizontal arm along the bottom
    const outer = `M${pt(0, ry)}A${P(t / 2)} ${P(ry)} 0 0 0 ${pt(t, ry)}V${P(h - t)}H${P(w - ry)}A${P(ry)} ${P(t / 2)} 0 0 1 ${pt(w - ry, h)}H0Z`;
    return [
      { d: outer, fill: 'hgrad' },
      { d: ellipsePath(t / 2, ry, t / 2, ry), fill: 'dark' },
      { d: ellipsePath(w - ry, h - t / 2, ry, t / 2), fill: 'dark' },
    ];
  } },
  arrow3d: { title: '3D Arrow', parts: (w, h) => {
    const d = Math.min(w, h) * 0.16;                 // extrusion offset
    const W = w - d, H = h - d;                      // shrink so offset copy stays in-bounds
    // horizontal block-arrow pointing right, y-centered within (0,0,W,H)
    const headW = W * 0.38, shaftH = H * 0.42, shaftY = (H - shaftH) / 2;
    const arrowPts = (ox, oy) => [
      [ox, oy + shaftY], [ox + W - headW, oy + shaftY],
      [ox + W - headW, oy], [ox + W, oy + H / 2],
      [ox + W - headW, oy + H], [ox + W - headW, oy + shaftY + shaftH],
      [ox, oy + shaftY + shaftH],
    ];
    return [
      { d: poly(arrowPts(d, d)), fill: 'dark' },       // back/shadow face, offset down-right
      { d: poly(arrowPts(0, 0)), fill: 'hgrad' },      // front face
    ];
  } },
  rod3d: { title: '3D Rod', parts: (w, h) => {
    const ry = Math.min(h * 0.32, w * 0.14);
    return [
      { d: roundedRect(0, h / 2 - ry, w, ry * 2, ry), fill: 'vgrad' },
      { d: ellipsePath(ry, h / 2, ry, ry), fill: 'light' },
      { d: ellipsePath(w - ry, h / 2, ry, ry), fill: 'dark' },
    ];
  } },
};

export const SHAPE_2D_KEYS = Object.keys(SHAPES_2D);
export const SHAPE_3D_KEYS = Object.keys(SHAPES_3D);
export const isShape2D = (t) => !!SHAPES_2D[t];
export const isShape3D = (t) => !!SHAPES_3D[t];
export const isLibraryShape = (t) => isShape2D(t) || isShape3D(t);

/* --------------------------------- colour --------------------------------- */

const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [80, 120, 220];
};
const toHex = (r, g, b) => '#' + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');

/** amount > 0 mixes towards white, < 0 towards black (|amount| ≤ 1). */
export const shade = (hex, amount) => {
  const [r, g, b] = hexToRgb(hex);
  const t = amount > 0 ? 255 : 0, k = Math.abs(amount);
  return toHex(r + (t - r) * k, g + (t - g) * k, b + (t - b) * k);
};

/** The three tones every 3D part is drawn with. */
export const tones3D = (hex) => ({ light: shade(hex, 0.38), base: hex, dark: shade(hex, -0.32), darker: shade(hex, -0.5) });

/** The colour a 3D object is drawn in: its fill, or its stroke colour when there is no fill. */
export const bodyColour3D = (shape) =>
  (shape.fillColor && shape.fillColor !== 'none') ? shape.fillColor : (shape.strokeColor || '#4f8ef7');

/* -------------------------------- freeform -------------------------------- */

/** Ramer–Douglas–Peucker: keep the points that shape the line, drop the jitter. */
export const simplifyPoints = (pts, tol) => {
  if (pts.length < 3) return pts.slice();
  const sq = (a, b) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
  const distToSeg = (p, a, b) => {
    const l2 = sq(a, b);
    if (!l2) return Math.sqrt(sq(p, a));
    let u = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / l2;
    u = Math.max(0, Math.min(1, u));
    return Math.sqrt(sq(p, { x: a.x + u * (b.x - a.x), y: a.y + u * (b.y - a.y) }));
  };
  const keep = new Uint8Array(pts.length); keep[0] = 1; keep[pts.length - 1] = 1;
  const stack = [[0, pts.length - 1]];
  while (stack.length) {
    const [i, j] = stack.pop();
    let best = -1, bestD = tol;
    for (let k = i + 1; k < j; k++) { const d = distToSeg(pts[k], pts[i], pts[j]); if (d > bestD) { bestD = d; best = k; } }
    if (best > 0) { keep[best] = 1; stack.push([i, best], [best, j]); }
  }
  return pts.filter((_, i) => keep[i]);
};

/**
 * Path for a freeform figure. `pts` are fractions (0..1) of the w×h box; `smooth` runs a
 * Catmull-Rom spline through the nodes, otherwise straight segments.
 */
export const freeformPath = (pts, w, h, closed, smooth = true) => {
  const n = pts.length;
  if (!n) return '';
  const P = (i) => { const p = pts[closed ? ((i % n) + n) % n : Math.max(0, Math.min(n - 1, i))]; return { x: p.x * w, y: p.y * h }; };
  let d = `M${pt(P(0).x, P(0).y)}`;
  if (n === 1) return d;
  const segs = closed ? n : n - 1;
  for (let i = 0; i < segs; i++) {
    const p0 = P(i - 1), p1 = P(i), p2 = P(i + 1), p3 = P(i + 2);
    if (smooth && n > 2) {
      const c1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 };
      const c2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 };
      d += `C${pt(c1.x, c1.y)} ${pt(c2.x, c2.y)} ${pt(p2.x, p2.y)}`;
    } else {
      d += `L${pt(p2.x, p2.y)}`;
    }
  }
  return closed ? d + 'Z' : d;
};

/** Fit points (any units) into their bounding box → { x1, y1, x2, y2, pts: fractions }. */
export const normalizeFreeform = (points) => {
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  for (const p of points) { if (p.x < x1) x1 = p.x; if (p.x > x2) x2 = p.x; if (p.y < y1) y1 = p.y; if (p.y > y2) y2 = p.y; }
  const w = Math.max(x2 - x1, 0.5), hh = Math.max(y2 - y1, 0.5);
  return { x1, y1, x2: x1 + w, y2: y1 + hh, pts: points.map(p => ({ x: (p.x - x1) / w, y: (p.y - y1) / hh })) };
};
